import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, Pipe, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  ErrorStateService,
  MarketplaceQuery,
  MarketplaceItemStatus,
  MarketplaceItemExtended,
  TermsAndCondtion,
  CartQuery,
  CartService,
  MarketplaceAttributeCartItem,
  MarketplaceBasketCustomFieldModel,
  AttributeValue,
  ActionLabels,
  LoyaltyStatusService,
  FgbError,
} from '@fgb/core';
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CartModalComponent } from '@fgb/portal-component-library/src/lib/loyalty/cart-item/cart-modal';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { MarketplacePurchaseConfirmationComponent } from '../marketplace-purchase-confirmation/marketplace-purchase-confirmation.component';

@Pipe({
  name: 'ecashDisabled',
})
export class EcashButtonDisabled {
  transform(purchaseLimit: number, item: MarketplaceItemExtended): boolean {
    return purchaseLimit <= 0 || item.Status != MarketplaceItemStatus.Available;
  }
}

@Pipe({
  name: 'inputActive',
})
export class InputActive {
  private statusService = inject(LoyaltyStatusService);

  transform(purchaseLimit: number, item: MarketplaceItemExtended): boolean {
    const status = this.statusService.getStatus(item);
    return purchaseLimit > 1 || item.Status != MarketplaceItemStatus.Available || !status.disabled;
  }
}

@Component({
  selector: 'fgbcl-marketplace-actions',
  templateUrl: './marketplace-actions.component.html',
  styleUrls: ['./marketplace-actions.component.scss'],
})
export class MarketplaceActionsComponent implements OnInit, OnDestroy {
  @Input() marketplaceItem: MarketplaceItemExtended;
  @Input() marketplaceItemType: string;
  @Input() attributeMappingID: number | undefined;
  @Input() selectedAttributes: AttributeValue[];
  @Input() validCustomFields: MarketplaceBasketCustomFieldModel[] | undefined = [];
  @Input() hasECash: boolean;
  @Input() theme: string;
  @Input() actionLabels = ActionLabels;
  @Input() availableStock: number;
  @Input() isCheckTC: boolean;

  @Output() purchaseLoading = new EventEmitter<boolean>(false);
  @Output() hasSumbittedForm = new EventEmitter<boolean>(false);

  originalStatus: MarketplaceItemStatus;
  marketplaceTerms: TermsAndCondtion;
  statusText: string;
  purchaseLimit$: Observable<number>;
  limitMessage$: Observable<string>;
  cardTokenCount: number = 0;
  private _acceptedTerms: boolean = false;
  isActionClicked: boolean = false;
  isUnredeemble: boolean = false;
  stockCount: number;
  purchaseQuantity = 1;

  marketplaceItemStatus = MarketplaceItemStatus;

  disableCart$: Observable<boolean | undefined>;

  private _destroyed$ = new Subject<void>();

  constructor(
    private marketplaceQuery: MarketplaceQuery,
    public errorService: ErrorStateService,
    private modalService: NgbModal,
    private cartQuery: CartQuery,
    private cartService: CartService,
    private router: Router
  ) {}

  ngOnInit() {
    this._setUpLocalVariables();
    this.updatePurchaseData();
  }

  updateTermsAndCondtions(accepted: boolean) {
    this._acceptedTerms = accepted;
    this.updatePurchaseValidation(this.marketplaceItem);
  }

  updateCustomFields(customFields: MarketplaceBasketCustomFieldModel[] | undefined) {
    this.validCustomFields = customFields;
    this.updatePurchaseValidation(this.marketplaceItem);
  }

  updatePurchaseData(purchaseQuantity?: number, attributeMappingID?: number) {
    if (purchaseQuantity) {
      this.purchaseQuantity = purchaseQuantity;
    }
    if (attributeMappingID) {
      this.attributeMappingID = attributeMappingID;
    }
    this._updatePurchaseLimit();
    this.updatePurchaseValidation(this.marketplaceItem);
  }

  private _setUpLocalVariables() {
    this.originalStatus = this.marketplaceItem.Status;
    this.marketplaceTerms = new TermsAndCondtion(
      this.marketplaceItem.TermsAndConditions,
      this.marketplaceItem.ShowTermsAndConditions,
      this.marketplaceItem.TermsAndConditionsType
    );
  }

  private _updateItemLocalStatus(purchaseLimit?: number) {
    let cartItemQty = this.cartQuery.getCartItemPurchaseQuantity(this.marketplaceItem.Id, this.attributeMappingID);

    if (
      this.availableStock < 1 &&
      this.originalStatus == MarketplaceItemStatus.Available &&
      !this.marketplaceItem.UnlimitedStock
    ) {
      this.marketplaceItem.Status = MarketplaceItemStatus.SoldOut;
    } else if (this._isMaxStockInBasket(cartItemQty) || this._isLimitReachedInBasket(cartItemQty, purchaseLimit)) {
      this.marketplaceItem.Status = MarketplaceItemStatus.AllInBasket;
    } else {
      this.marketplaceItem.Status = this.originalStatus;
    }
    this._updateStatusText(this.marketplaceItem);
  }

  private _isMaxStockInBasket(cartItemQty: number): boolean {
    const mustCompare = !!this.availableStock && !!cartItemQty;
    const availableStockLocal = this.availableStock - cartItemQty;
    const isAboveLocalStock = this.purchaseQuantity > availableStockLocal;

    return mustCompare && isAboveLocalStock;
  }

  private _isLimitReachedInBasket(cartItemQty: number, purchaseLimit?: number) {
    let availableLimit: number;
    let isAboveLimit = false;
    if (purchaseLimit) {
      availableLimit = purchaseLimit - cartItemQty;
      isAboveLimit = this.purchaseQuantity > availableLimit;
    }
    return isAboveLimit;
  }

  private _updateStatusText(marketplaceItem: MarketplaceItemExtended) {
    const today = DateTime.utc();
    const startDate = DateTime.fromISO(marketplaceItem.ItemStartDate as string, { zone: 'utc' });

    if (marketplaceItem.Status === MarketplaceItemStatus.ComingSoon && startDate <= today) {
      marketplaceItem.Status = MarketplaceItemStatus.Available;
      this.isUnredeemble = false;
    }

    switch (marketplaceItem.Status) {
      case MarketplaceItemStatus.SoldOut:
        this.statusText = 'marketplace.redeem.sold.out';
        this.isUnredeemble = true;
        break;
      case MarketplaceItemStatus.NotEnoughPoints:
        this.statusText = 'marketplace.redeem.not.enough.points';
        this.isUnredeemble = true;
        break;
      case MarketplaceItemStatus.ComingSoon:
        this.statusText = 'marketplace.redeem.coming.soon';
        this.isUnredeemble = true;
        break;
      case MarketplaceItemStatus.MemberLimitReached:
        this.statusText = 'marketplace.redeem.member.limit';
        this.isUnredeemble = true;
        break;
      case MarketplaceItemStatus.AllInBasket:
        this.statusText = 'marketplace.redeem.all.in.basket';
        this.isUnredeemble = false;
        break;
      default:
        if (this.marketplaceItem.theme === 'mp-event') {
          this.statusText = 'marketplace.redeem.event';
        } else if (this.marketplaceItem.theme === 'mp-virtual-event') {
          this.statusText = 'marketplace.redeem.virtual.event';
        } else if (this.marketplaceItem.theme === 'mp-product') {
          this.statusText = 'marketplace.redeem.product';
        } else {
          this.statusText = 'marketplace.redeem.marketplace';
        }
        this.isUnredeemble = false;
    }
  }

  private _updatePurchaseLimit() {
    this.purchaseLimit$ = this.marketplaceQuery.selectPurchaseLimit(this.marketplaceItem.Id, undefined, this.attributeMappingID);
    this.limitMessage$ = this.marketplaceQuery.selectPurchaseLimitMessage(
      this.marketplaceItem.Id,
      undefined,
      this.attributeMappingID
    );
  }

  updatePurchaseValidation(item: MarketplaceItemExtended) {
    this.disableCart$ = this.cartQuery.selectMemberMarketplaceCartItem(item.Id, this.attributeMappingID).pipe(
      takeUntil(this._destroyed$),
      mergeMap((currentCartItem) => {
        return this.purchaseLimit$.pipe(
          tap((purchaseLimit) => this._updateItemLocalStatus(purchaseLimit)),
          map((purchaseLimit) => {
            const currentCartItemQuantity = currentCartItem ? currentCartItem.purchaseQuantity : 0;
            const assumedPurchaseQuantity = this.purchaseQuantity + currentCartItemQuantity;

            const cartQuantityExceedsStock = currentCartItem && assumedPurchaseQuantity > this.availableStock;
            const cartQuantityExceedsLimit = currentCartItem && assumedPurchaseQuantity > purchaseLimit;
            const isUnavailable = item.Status !== MarketplaceItemStatus.Available;
            const insufficientQuantity =
              (purchaseLimit <= 0 || this.purchaseQuantity < (purchaseLimit > 0 ? 1 : 0)) && !this.marketplaceItem.UnlimitedStock;

            const isCartDisabled =
              isUnavailable ||
              insufficientQuantity ||
              cartQuantityExceedsStock ||
              cartQuantityExceedsLimit ||
              (item.ShowTermsAndConditions && !this._acceptedTerms) ||
              (item.CustomFields && !this.validCustomFields);
            return isCartDisabled;
          })
        );
      })
    );
  }

  addProductToCart(isDisabled: boolean, openCartModal?: boolean) {
    this.isActionClicked = true;
    if (isDisabled) return;

    this.errorService.clearErrors();
    let purchaseLimit = 0;
    this.purchaseLimit$.subscribe((x) => (purchaseLimit = x)).unsubscribe();
    let cartPurchaseQty = this.cartQuery.getCartItemPurchaseQuantity(this.marketplaceItem.Id, this.attributeMappingID);

    this.hasSumbittedForm.emit(true);
    if (this.marketplaceItem.CustomFields && !this.validCustomFields) return;

    if (!!cartPurchaseQty) {
      if (this._isMaxStockInBasket(cartPurchaseQty)) {
        this.errorService.addError('You have added the maximum stock to your basket');
        return;
      } else if (this._isLimitReachedInBasket(cartPurchaseQty, purchaseLimit)) {
        this.errorService.addError('Limit Reached');
        return;
      } else {
        this.addItemToCartService();
      }
    } else if (this.purchaseQuantity > this.availableStock) {
      this.errorService.addError('Sold Out');
      return;
    } else {
      if (this.marketplaceItem.ShowTermsAndConditions && !!this.marketplaceItem.TermsAndConditions) {
        this.openPurchaseConfirmationModal(false, 'confirmContent', true, openCartModal);
      } else {
        this.addItemToCartService();
        if (openCartModal) {
          this.openCartModal();
        }
      }
    }
  }

  openCartModal() {
    const unvalidCustomFields = this.marketplaceItem.CustomFields && !this.validCustomFields;
    if (unvalidCustomFields) return;

    this.modalService.open(CartModalComponent);
  }

  openPurchaseConfirmationModal(isDisabled: boolean, templateName: string, addToCart?: boolean, openCartModal?: boolean) {
    const isMerchantItem = this.marketplaceItem.ShippingEnabled || this.marketplaceItem.MerchantValue;
    if (isMerchantItem) {
      this.cartService.removeAllCartItems();
      this.addItemToCartService();
      this.router.navigate(['/checkout']);
    } else {
      this.isActionClicked = true;
      if (isDisabled) return;

      this.hasSumbittedForm.emit(true);

      const unvalidCustomFields = this.marketplaceItem.CustomFields && !this.validCustomFields;
      if (unvalidCustomFields) return;

      const modal = this.modalService.open(MarketplacePurchaseConfirmationComponent, {
        ariaLabelledBy: 'modal-basic-title',
        centered: true,
        windowClass: 'reward-confirm-modal',
      });
      modal.componentInstance.templateSelected = templateName;
      modal.componentInstance.marketplaceItem = this.marketplaceItem;
      modal.componentInstance.marketplaceItemType = this.marketplaceItemType;
      modal.componentInstance.attributeMappingID = this.attributeMappingID;
      modal.componentInstance.selectedAttributes = this.selectedAttributes;
      modal.componentInstance.purchaseQuantity = this.purchaseQuantity;
      modal.componentInstance.validCustomFields = this.validCustomFields;
      modal.componentInstance.purchaseLoading.pipe(takeUntil(this._destroyed$)).subscribe((loadingState: boolean) => {
        this.purchaseLoading.emit(loadingState);
        if (!loadingState) {
          this.modalService.dismissAll();
        }
      });
      if (addToCart) {
        modal.componentInstance.addToCart = true;
        modal.componentInstance.addToCartTermsConfirmed.pipe(takeUntil(this._destroyed$)).subscribe((termsConfirmed: boolean) => {
          if (termsConfirmed) {
            this.addItemToCartService();
            if (openCartModal) {
              this.openCartModal();
            }
          }
        });
      }
    }
  }

  isEcashButtonDisabled(purchaseLimit: number): boolean {
    return purchaseLimit <= 0 || this.marketplaceItem.Status != MarketplaceItemStatus.Available;
  }

  addItemToCartService() {
    const attributeCart: MarketplaceAttributeCartItem = {
      AttributeMappingId: this.attributeMappingID,
      LocalAttributes: this.selectedAttributes,
    };

    this.cartService.addMarketplaceItemToCart(this.marketplaceItem, this.purchaseQuantity, attributeCart, this.validCustomFields);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onErrorChange(err: any) {
    if (err) {
      throw new FgbError({ message: err });
    }
  }
}
